import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import axios from "axios";
import categoryService from "../../repository/categoryRepository";

const AddSubcategory = ({ open, onClose, onSave, mainCategoryId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    mainCategoryNameForSubCategory: "",
    subCategoryName: "",
    subCategoryDescription: "",
    subCategoryIcon: null,
    subCategoryIconName: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [error, setError] = useState("");

  const [sizeType, setSizeType] = useState("");
  const [romanSize, setRomanSize] = useState("");
  const [numberSize, setNumberSize] = useState("");

  const fetchCategories = useCallback(async () => {
    try {
      if (!mainCategoryId) {
        setError("Main Category ID is missing");
        return;
      }

      const response = await categoryService.fetchCategories(mainCategoryId);
      if (response && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        throw new Error("Invalid data format for categories");
      }
    } catch (err) {
      setError("Failed to Fetch Categories");
    }
  }, [mainCategoryId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    setFormData((prev) => ({
      ...prev,
      subCategoryIcon: file,
      subCategoryIconName: file ? file.name : "",
    }));
  };

  const handleSubmit = async () => {
    try {
      const apiEndpoint =
        "https://backend.hindustanwardrobe.in/admin/addSubCategory";
      const data = new FormData();
      const {
        mainCategoryNameForSubCategory,
        subCategoryName,
        subCategoryDescription,
        subCategoryIcon,
      } = formData;

      const selectedCategory = categories.find(
        (category) => category.categoryName === mainCategoryNameForSubCategory,
      );

      if (!selectedCategory) {
        setSnackbarMessage("Please select a valid category.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }

      const categoryId = selectedCategory.categoryId;
      const categoryName = selectedCategory.mainCategoryName;

      data.append("categoryId", categoryId);
      data.append("categoryName", categoryName);
      data.append("subCategoryName", subCategoryName);
      data.append("description", subCategoryDescription);

      let sizeTypeValue = sizeType || "No Size";
      data.append("sizeType", sizeTypeValue);

      if (sizeType === "Roman (S, M, L, XL)") {
        data.append("selectedSize", romanSize || "");
      } else if (sizeType === "Number (28, 30, 40)") {
        data.append("selectedSize", numberSize || "");
      } else if (sizeType === "Dual Size (Upper and Lower)") {
        data.append("selectedSize", romanSize || numberSize || "");
      } else {
        data.append("selectedSize", "");
      }

      if (subCategoryIcon) {
        data.append("imageURL", subCategoryIcon);
      }

      const response = await categoryService.addSubCategory(data);

      if (response.success) {
        setSnackbarMessage("Subcategory added successfully!");
        setSnackbarSeverity("success");
        if (onSave && typeof onSave === "function") {
          onSave(response.data);
        }
        onClose();
      } else {
        setSnackbarMessage("Error adding subcategory.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      setSnackbarMessage("Error: " + error.message);
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          backgroundColor:
            colors.background || theme.palette.background.default,
          color: colors.text || theme.palette.text.primary,
        },
      }}
    >
      <DialogTitle>
        Add Subcategory
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Typography color="error">{error}</Typography>}
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option.categoryName || ""}
          onChange={(event, newValue) =>
            setFormData((prevState) => ({
              ...prevState,
              mainCategoryNameForSubCategory: newValue
                ? newValue.categoryName
                : "",
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Category"
              variant="outlined"
              sx={{
                mb: "10px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor:
                      theme.palette.mode === "light" ? "black" : "white",
                  },
                  "&:hover fieldset": {
                    borderColor:
                      theme.palette.mode === "light" ? "black" : "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor:
                      theme.palette.mode === "light" ? "black" : "white",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.mode === "light" ? "black" : "white",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: theme.palette.mode === "light" ? "black" : "white",
                },
              }}
            />
          )}
        />
        <TextField
          fullWidth
          name="subCategoryName"
          label="Subcategory Name"
          value={formData.subCategoryName}
          onChange={handleInputChange}
          sx={{
            mb: "10px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.mode === "light" ? "black" : "white",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.mode === "light" ? "black" : "white",
            },
          }}
        />
        <TextField
          fullWidth
          name="subCategoryDescription"
          label="Description"
          value={formData.subCategoryDescription}
          onChange={handleInputChange}
          sx={{
            mb: "10px",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.mode === "light" ? "black" : "white",
              },
            },
            "& .MuiInputLabel-root": {
              color: theme.palette.mode === "light" ? "black" : "white",
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.mode === "light" ? "black" : "white",
            },
          }}
        />

        {/* New Size Type Selection */}
        <TextField
          fullWidth
          select
          label="Select Size Type"
          value={sizeType}
          onChange={(e) => setSizeType(e.target.value)}
          sx={{
            mb: "10px",
          }}
        >
          <MenuItem value="Roman (S, M, L, XL)">
            Roman Sizes (S, M, L, XL)
          </MenuItem>
          <MenuItem value="Number (28, 30, 40)">
            Number Sizes (28, 30, 40)
          </MenuItem>
          <MenuItem value="Dual Size (Upper and Lower)">
            Dual Size (Upper and Lower)
          </MenuItem>
          <MenuItem value="No Size">No Size</MenuItem>
        </TextField>

        {/* Upload Icon */}
        <Box display="flex" flexDirection="column" gap="10px" marginTop={2}>
          <Box display="flex" alignItems="center" gap="10px">
            <Button
              variant="contained"
              component="label"
              color="secondary"
              startIcon={<CloudUploadIcon />}
            >
              Upload Icon
              <input
                type="file"
                name="subCategoryIcon"
                hidden
                onChange={handleIconUpload}
              />
            </Button>
            {formData.subCategoryIconName && (
              <Typography variant="body2" color={theme.palette.text.primary}>
                Uploaded: {formData.subCategoryIconName}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={handleSubmit}>
          Add Subcategory
        </Button>
      </DialogActions>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddSubcategory;
